// Copyright 2020 Drivetrain Hub LLC
// Tool geometry functions for chart.js.

import math from "../math";
import gear from "../gear";
import { mirrorAboutX, points, points2Arrays, rotatePoints2, rotationMatrix2 } from "../coord";
import { circleCurve, involuteCurve, tipCornerCurve } from "./shapes";

function pitchFcn(module) {
  return Math.PI * module
}

// region BASIC RACK

function basicRackFullRootRadiusWithoutUndercut(pitch, pressureAngle, dedendum, undercutSize) {
  // Calculate the maximum allowable full root fillet radius, not coefficient.
  // Calculates radius between root mid-point of tooth spacewidth and tangent to profile.
  // Pressure angle arg must be in radians.

  const pressureAngleMinus90 = pressureAngle - Math.PI / 2;
  const csc = 1 / Math.sin(pressureAngle);
  const sec = 1 / Math.cos(pressureAngle);
  const cosine = Math.cos(pressureAngleMinus90);
  const sine = Math.sin(pressureAngleMinus90);
  const s = pitch / 2;  // tooth spacewidth @ datum line
  const hf = dedendum;
  const u = undercutSize;

  const fullRootRadius = (s * csc - 2 * hf * sec + 2 * u * csc * sec) / (-2 * sec + 2 * cosine * sec - 2 * csc * sine);

  // subtract small amount to avoid numerical errors
  return fullRootRadius - 1e-6
}

export function basicRackMaxRootRadius(
  {moduleNormal, pressureAngle, dedendumCoefficient, undercutSize, undercutAngle}, unitAngle) {
  // Calculate the maximum allowable root fillet radius, not coefficient.
  // Maximum value of root radius is not necessarily a full root.
  // Valid for cases with and without undercut.

  pressureAngle = math.angle2rad(pressureAngle, unitAngle);
  undercutAngle = math.angle2rad(undercutAngle, unitAngle);
  undercutSize = parseFloat(undercutSize);

  const pitch = pitchFcn(moduleNormal);
  const dedendum = dedendumCoefficient * moduleNormal;
  let maxRootRadius = basicRackFullRootRadiusWithoutUndercut(pitch, pressureAngle, dedendum, undercutSize);

  if (undercutSize !== 0) {
    // check if full root is valid
    const toothSpacewidth = pitch / 2;
    const heightFilletTangent = maxRootRadius * (1 - Math.sin(undercutAngle));
    const heightProfileAtFilletTangent = maxRootRadius + (dedendum - maxRootRadius) -
      (toothSpacewidth / 2 - maxRootRadius * Math.cos(undercutAngle)) / Math.tan(pressureAngle);

    if (heightFilletTangent > heightProfileAtFilletTangent) {
      // Cannot have a full root radius for the given undercut angle.
      // Without changing other params, fillet radius must reduce and root bottom must have flat portion.
      maxRootRadius = undercutSize / (1 - Math.cos(pressureAngle - undercutAngle));
    }
  }

  return maxRootRadius
}

function basicRackCoordinatesWithoutUndercut(
  {moduleNormal, pressureAngle, addendumCoefficient, dedendumCoefficient, rootRadiusCoefficient}, unitAngle, n) {
  // Cartesian coordinates for a basic rack without undercut.
  // Coordinates of master tooth are computed left-to-right, with the origin on the datum line at mid-tooth.
  // Draw the number of teeth specified by n, centering the coordinates on Y-axis.
  // Reference notebooks-gears basic_rack.py.

  const pitch = pitchFcn(moduleNormal);
  const addendum = addendumCoefficient * moduleNormal;
  const dedendum = dedendumCoefficient * moduleNormal;
  const rootRadius = rootRadiusCoefficient * moduleNormal;
  pressureAngle = math.angle2rad(pressureAngle, unitAngle);

  const rootRadiusHeight = rootRadius - rootRadius * Math.sin(pressureAngle);
  const dedendumProfileHeight = dedendum - rootRadiusHeight;

  const ycRho = - dedendum + rootRadius;
  const xcRhoRl = pitch / 4 - dedendumProfileHeight * Math.tan(pressureAngle) - rootRadius * Math.cos(pressureAngle);
  // ==> calculated for the right tooth, left root; indicated by `Rl` suffix
  // ==> x-value >= 0 for valid values of root radius; bottom clearance limit may further constrain

  if (xcRhoRl < 0) {
    // Root geometry is not feasible.
    return null;
  }

  // root land - left-half
  const xcL = -pitch / 2 + xcRhoRl;
  const xRootLHalf = [-pitch / 2, xcL];
  const yRootLHalf = [-dedendum, -dedendum];

  // root radius - left
  const [xLr, yLr] = circleCurve(rootRadius, -Math.PI / 2, -pressureAngle, xcL, ycRho);

  // tip land
  const xTip = [- pitch / 4 + addendum * Math.tan(pressureAngle), pitch / 4 - addendum * Math.tan(pressureAngle)];
  const yTip = [addendum, addendum];

  // root radius - right
  const xcR = pitch / 2 - xcRhoRl;
  const [xRr, yRr] = circleCurve(rootRadius, -Math.PI + pressureAngle, -Math.PI / 2, xcR, ycRho);

  // root land - right-half
  const xRootRHalf = [xcR, pitch / 2];
  const yRootRHalf = [-dedendum, -dedendum];

  // combine coordinate data
  let xCoord = [...xRootLHalf, ...xLr, ...xTip, ...xRr, ...xRootRHalf];
  let yCoord = [...yRootLHalf, ...yLr, ...yTip, ...yRr, ...yRootRHalf];

  // draw multiple teeth
  let xCoordNew, xOffset;
  for (let i=2; i <= n; i++) {
    xOffset = pitch * (i - 1);
    xCoordNew = [...xCoord];
    xCoordNew = xCoord.map(x => x + xOffset);
    xCoord.push(...xCoordNew);
    yCoord.push(...yCoord);
  }

  // center on Y-axis
  const xCentering = - pitch / 2 * (n - 1);
  xCoord = xCoord.map(x => x + xCentering);

  return [xCoord, yCoord]
}

function basicRackCoordinatesWithUndercut(
  {moduleNormal, pressureAngle, addendumCoefficient, dedendumCoefficient, rootRadiusCoefficient,
    undercutSize, undercutAngle}, unitAngle, n) {
  // Cartesian coordinates for a basic rack with undercut.
  // Coordinates of master tooth are computed left-to-right, with the origin on the datum line at mid-tooth.
  // Draw the number of teeth specified by n, centering the coordinates on Y-axis.
  // Reference notebooks-gears basic_rack.py.

  const pitch = pitchFcn(moduleNormal);
  const addendum = addendumCoefficient * moduleNormal;
  const dedendum = dedendumCoefficient * moduleNormal;
  const rootRadius = rootRadiusCoefficient * moduleNormal;
  pressureAngle = math.angle2rad(pressureAngle, unitAngle);
  undercutAngle = math.angle2rad(undercutAngle, unitAngle);

  // height of root radius, undercut, and dedendum tooth profile
  const rootRadiusHeight = rootRadius - rootRadius * Math.sin(undercutAngle);
  const xQf = rootRadius * (1 - Math.cos(pressureAngle - undercutAngle));
  const lU = (undercutSize - xQf) / Math.sin(pressureAngle - undercutAngle);
  const undercutProfileHeight = lU * Math.cos(undercutAngle);
  const undercutHeightFromBotton = rootRadiusHeight + undercutProfileHeight;
  const dedendumProfileHeight = dedendum - undercutHeightFromBotton;

  const ycRho = -dedendum + rootRadius;
  const yUndercut = -dedendum + undercutHeightFromBotton;

  const xcRhoRl = pitch / 4
    - dedendumProfileHeight * Math.tan(pressureAngle)
    - undercutProfileHeight * Math.tan(undercutAngle)
    - rootRadius * Math.cos(undercutAngle);
  // ==> calculated for the right tooth, left root; indicated by Rl suffix
  // ==> x-value >= 0 for valid values of root radius; bottom clearance limit may further constrain

  if (xcRhoRl < 0) {
    // Root geometry is not feasible.
    return null;
  }

  // root land - left-half
  const xcL = -pitch / 2 + xcRhoRl;
  const xRootLHalf = [-pitch / 2, xcL];
  const yRootLHalf = [-dedendum, -dedendum];

  // root radius - left
  const [xLr, yLr] = circleCurve(rootRadius, -Math.PI / 2, -undercutAngle, xcL, ycRho);

  // undercut point - left
  const xLu = [- pitch / 4 - dedendumProfileHeight * Math.tan(pressureAngle)];
  const yLu = [yUndercut];

  // tip land
  const xTip = [- pitch / 4 + addendum * Math.tan(pressureAngle), pitch / 4 - addendum * Math.tan(pressureAngle)];
  const yTip = [addendum, addendum];

  // undercut point - right
  const xRu = [pitch / 4 + dedendumProfileHeight * Math.tan(pressureAngle)];
  const yRu = [yUndercut];

  // root radius - right
  const xcR = pitch / 2 - xcRhoRl;
  const [xRr, yRr] = circleCurve(rootRadius, -Math.PI + undercutAngle, -Math.PI / 2, xcR, ycRho);

  // root land - right-half
  const xRootRHalf = [xcR, pitch / 2];
  const yRootRHalf = [-dedendum, -dedendum];

  // combine coordinate data
  let xCoord = [...xRootLHalf, ...xLr, ...xLu, ...xTip, ...xRu, ...xRr, ...xRootRHalf];
  let yCoord = [...yRootLHalf, ...yLr, ...yLu, ...yTip, ...yRu, ...yRr, ...yRootRHalf];

  // draw multiple teeth
  let xCoordNew, xOffset;
  for (let i=2; i <= n; i++) {
    xOffset = pitch * (i - 1);
    xCoordNew = [...xCoord];
    xCoordNew = xCoord.map(x => x + xOffset);
    xCoord.push(...xCoordNew);
    yCoord.push(...yCoord);
  }

  // center on Y-axis
  const xCentering = - pitch / 2 * (n - 1);
  xCoord = xCoord.map(x => x + xCentering);

  return [xCoord, yCoord]
}

export function basicRackCoordinates(
  {moduleNormal, pressureAngle, addendumCoefficient, dedendumCoefficient, rootRadiusCoefficient,
    undercutSize, undercutAngle}, unitAngle, n=1) {
  // Calculate basic rack coordinates.
  // arg1: Basic rack geometry parameters.
  // arg2: String of angular units of input args.
  // arg3: Number of rack teeth to compute.
  // Handles cases with and without undercut.
  // return: Planar coordinates of three (3) basic rack teeth, centered on positive Y-axis.

  if (parseFloat(undercutSize) === 0) {
    return basicRackCoordinatesWithoutUndercut({
      moduleNormal, pressureAngle, addendumCoefficient, dedendumCoefficient, rootRadiusCoefficient}, unitAngle, n);
  } else {
    return basicRackCoordinatesWithUndercut({
      moduleNormal, pressureAngle, addendumCoefficient, dedendumCoefficient, rootRadiusCoefficient,
      undercutSize, undercutAngle}, unitAngle, n);
  }
}

// let BasicRack = function(moduleNormal, pressureAngle, addendum, dedendum) {
//   // PLACEHOLDER update input args based on Python BasicRack class
//   // PLACEHOLDER calculate geometric properties of basic rack (reference Python)
// };
//
// BasicRack.prototype.render = function (context, drawNumberOfTeeth = 2) {
//   // PLACEHOLDER draw canvas path for specified number of basic rack teeth
// };

// endregion

// region HOB

export function hobMaxTipRadius(
  {moduleNormal, pressureAngleNormal, addendumCoefficient, protuberanceSize, protuberanceAngle}, unitAngle) {
  // map tool to basic rack params
  const dedendumCoefficient = addendumCoefficient;
  const pressureAngle = pressureAngleNormal;
  const undercutSize = protuberanceSize;
  const undercutAngle = protuberanceAngle;

  return basicRackMaxRootRadius({moduleNormal, pressureAngle, dedendumCoefficient, undercutSize, undercutAngle}, unitAngle);
}

function hobCoordinatesWithoutProtuberance(
  {moduleNormal, pressureAngleNormal, addendumCoefficient, dedendumCoefficient, tipRadiusCoefficient}, unitAngle, n) {
  // Calculate hob coordinates as the inverse of a basic rack.

  return basicRackCoordinatesWithoutUndercut({
    moduleNormal: moduleNormal,
    pressureAngle: pressureAngleNormal,
    addendumCoefficient: dedendumCoefficient,  // inverse
    dedendumCoefficient: addendumCoefficient,  // inverse
    rootRadiusCoefficient: tipRadiusCoefficient,
  }, unitAngle, n)
}

function hobCoordinatesWithProtuberance(
  {moduleNormal, pressureAngleNormal, addendumCoefficient, dedendumCoefficient, tipRadiusCoefficient,
    protuberanceSize, protuberanceAngle}, unitAngle, n) {
  // Calculate protuberance hob coordinates as the inverse of an undercut basic rack.

  return basicRackCoordinatesWithUndercut({
    moduleNormal: moduleNormal,
    pressureAngle: pressureAngleNormal,
    addendumCoefficient: dedendumCoefficient,  // inverse
    dedendumCoefficient: addendumCoefficient,  // inverse
    rootRadiusCoefficient: tipRadiusCoefficient,
    undercutSize: protuberanceSize,
    undercutAngle: protuberanceAngle,
  }, unitAngle, n)
}

export function hobCoordinates(
  {moduleNormal, pressureAngleNormal, addendumCoefficient, dedendumCoefficient, tipRadiusCoefficient,
    protuberanceSize, protuberanceAngle}, unitAngle, n=1) {
  // Calculate hob coordinates.
  // arg1: Hob tool geometry parameters.
  // arg2: String of angular units of input args.
  // arg3: Number of tool teeth to compute.
  // Handles cases with and without protuberance.
  // return: Planar coordinates of three (3) hob tool teeth, centered on negative Y-axis.

  if (parseFloat(protuberanceSize) === 0) {
    return hobCoordinatesWithoutProtuberance({
      moduleNormal, pressureAngleNormal, addendumCoefficient, dedendumCoefficient, tipRadiusCoefficient}, unitAngle, n);
  } else {
    return hobCoordinatesWithProtuberance({
      moduleNormal, pressureAngleNormal, addendumCoefficient, dedendumCoefficient, tipRadiusCoefficient,
      protuberanceSize, protuberanceAngle}, unitAngle, n);
  }
}

// let Hob = function(normalModule, pressureAngle, addendum, dedendum) {
//   // PLACEHOLDER update input args based on Python Hob class
//   // PLACEHOLDER calculate geometric properties of hob (reference Python)
// };
//
// Hob.prototype.render = function (context) {
//   // PLACEHOLDER draw canvas path for tool profile
// };

// endregion

// region PINION CUTTER

export function pinionCutterCoordinates(
  {moduleNormal, numberOfTeeth, pressureAngleNormal, helixAngle, rootDiameter, tipDiameter, toothThicknessNormal,
    tipRadiusCoefficient}, {ttrRCenter, ttrLambdaCenter, ttrThetaEndTangent, ttrThetaEoi, rollAngleEoi},
  unitAngle, nInv=16) {
  // arg1: Pinion cutter geometry.
  // arg2: Computed pinion cutter geometry.
  // arg3: String of angular units of arg1.
  // Handles cases with and without protuberance.
  // return: Planar coordinates of three (3) pinion cutter teeth, centered on negative Y-axis.

  tipRadiusCoefficient = parseFloat(tipRadiusCoefficient);

  // angular conversions
  pressureAngleNormal = math.angle2rad(pressureAngleNormal, unitAngle);
  helixAngle = math.angle2rad(helixAngle, unitAngle);

  // base radius
  const moduleTransverse = gear.moduleTransverse1({moduleNormal, helixAngle});
  const theoreticalPitchDiameter = gear.theoreticalPitchDiameter1({numberOfTeeth, moduleTransverse});
  const pressureAngleTransverse = gear.pressureAngleTransverse1({pressureAngleNormal, helixAngle});
  const baseDiameter = gear.baseDiameter1({theoreticalPitchDiameter, pressureAngleTransverse});
  const baseRadius = baseDiameter / 2;

  // nominal tooth tip, without tip radius
  const rollAngleInvTip =  gear.diameter2RollAngle(baseDiameter, tipDiameter);

  // center tooth on x-axis
  const toothThicknessTransverse = gear.toothThicknessTransverse1({toothThicknessNormal, helixAngle});
  const toothHalfAngleBase = gear.toothBaseHalfAngleTransverse1({theoreticalPitchDiameter, pressureAngleTransverse, toothThicknessTransverse});
  const phiBase = -toothHalfAngleBase;

  // start and end of involute
  const rootRadius = rootDiameter / 2;
  const rollAngleI = rootRadius < baseRadius ? 0 : gear.diameter2RollAngle(baseDiameter, rootDiameter);
  let rollAngleF = rollAngleInvTip;

  // initialize vars
  let xTipCornerR=[], yTipCornerR=[], xTipCornerL=[], yTipCornerL=[];
  let xTipCornerTipR, yTipCornerTipR, xTipCornerTipL, yTipCornerTipL;
  const nTipCorner = 11;

  if (tipRadiusCoefficient !== 0) {
    // tip corner: R - inv-to-tip
    const tipCornerRadius = tipRadiusCoefficient * moduleNormal;
    [xTipCornerR, yTipCornerR] = tipCornerCurve(
      tipCornerRadius, helixAngle, ttrRCenter, ttrLambdaCenter, ttrThetaEoi, ttrThetaEndTangent, nTipCorner
    );

    // REFERENCE ONLY
    // const xTipCornerInvR = xTipCornerR[0];
    // const yTipCornerInvR = yTipCornerR[0];
    // const rTipCornerInv = Math.hypot(xTipCornerInvR, yTipCornerInvR);

    // DEV ONLY
    // console.log('xTipCornerR', xTipCornerR);
    // console.log('yTipCornerR', yTipCornerR);

    // tip corner: L - tip-to-inv
    const ptsTipCornerR = points(xTipCornerR, yTipCornerR);
    const ptsTipCornerL = mirrorAboutX(ptsTipCornerR).reverse();
    [xTipCornerL, yTipCornerL] = points2Arrays(ptsTipCornerL);

    // end of involute
    rollAngleF = rollAngleEoi;  // gear.diameter2RollAngle(baseRadius, rTipCornerInv);
  }

  // involute curve: R - base-to-tip
  const [xInvR, yInvR] = involuteCurve(baseRadius, rollAngleI, rollAngleF, phiBase, 0, 0, nInv);

  // involute curve: L - tip-to-base
  const ptsInvR = points(xInvR, yInvR);
  const ptsInvL = mirrorAboutX(ptsInvR).reverse();
  const [xInvL, yInvL] = points2Arrays(ptsInvL);

  // zero tip corner coordinates; keeps total coordinates the same, which helps chart animations
  if (tipRadiusCoefficient === 0) {
    xTipCornerR = Array(nTipCorner).fill(xInvR[nInv-1]);
    yTipCornerR = Array(nTipCorner).fill(yInvR[nInv-1]);
    xTipCornerL = Array(nTipCorner).fill(xInvL[0]);
    yTipCornerL = Array(nTipCorner).fill(yInvL[0]);
  }

  // coordinates at tip of tip corner
  xTipCornerTipR = xTipCornerR[nTipCorner - 1];
  yTipCornerTipR = yTipCornerR[nTipCorner - 1];
  xTipCornerTipL = xTipCornerL[0];
  yTipCornerTipL = yTipCornerL[0];

  // tip circle: R-to-L flank
  const tipRadius = tipDiameter / 2;
  const phiTipR = Math.atan2(yTipCornerTipR, xTipCornerTipR);
  const phiTipL = Math.atan2(yTipCornerTipL, xTipCornerTipL);
  const [xTip, yTip] = circleCurve(tipRadius, phiTipR, phiTipL, 0, 0, 5);

  // root circle: L-to-R+1
  const pitchAngle = gear.pitchAngleTransverse1(numberOfTeeth);
  const xInvRootL = xInvL[nInv-1];
  const yInvRootL = yInvL[nInv-1];
  let phiStart = Math.atan2(yInvRootL, xInvRootL);  // root of left involute
  let phiEnd = -phiStart + pitchAngle;  // root of right+1 involute
  let [xRoot, yRoot] = circleCurve(rootRadius, phiStart, phiEnd, 0, 0, 5);

  // combine curves for a single tooth
  let xT1 = [...xInvR, ...xTipCornerR, ...xTip, ...xTipCornerL, ...xInvL, ...xRoot];
  let yT1 = [...yInvR, ...yTipCornerR, ...yTip, ...yTipCornerL, ...yInvL, ...yRoot];

  // initialize orientation to have 2nd tooth at -pi / 2
  const rotMatNegY = rotationMatrix2(-Math.PI / 2 - pitchAngle);
  [xT1, yT1] = rotatePoints2(rotMatNegY, xT1, yT1);

  // draw 2 more teeth
  let xAll=[...xT1];
  let yAll=[...yT1];

  for (let i=1; i < 3; i++) {
    let rotMatPitch = rotationMatrix2(i * pitchAngle);
    let [xNext, yNext] = rotatePoints2(rotMatPitch, xT1, yT1);
    xAll = [...xAll, ...xNext];
    yAll = [...yAll, ...yNext];
  }

  // prepend root circle segment
  const iLast = yAll.length-1;
  phiEnd = Math.atan2(yAll[0], xAll[0]);
  phiStart = Math.atan2(yAll[iLast], -xAll[iLast]);
  [xRoot, yRoot] = circleCurve(rootRadius, phiStart, phiEnd, 0, 0, 5);
  xAll = [...xRoot, ...xAll];
  yAll = [...yRoot, ...yAll];

  return [xAll, yAll];
}

// endregion

// export default {
//   BasicRack,
//   Hob,
// }
